import React from 'react';
import { Eye, Mail, Send, Loader2, Search } from 'lucide-react';
import { formatPickupDate } from '../../utils/datetime';

const STATUS_COLORS = {
  open: 'bg-blue-100 text-blue-700', bidding: 'bg-yellow-100 text-yellow-700',
  booked: 'bg-emerald-100 text-emerald-700', completed: 'bg-slate-100 text-slate-600',
  cancelled: 'bg-red-100 text-red-700',
};
const STATUS_LABELS = {
  open: 'Offen', bidding: 'Angebote', booked: 'Gebucht', completed: 'Abgeschlossen', cancelled: 'Storniert',
};

export function AdminTransfersTab({ transfers, searchQuery, setSearchQuery, transferFilter, setTransferFilter, onSelectTransfer, resendEmail, resendingEmail }) {
  const filtered = transfers.filter(t => {
    if (transferFilter !== 'all' && t.status !== transferFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return t.customer_name?.toLowerCase().includes(q) || t.customer_email?.toLowerCase().includes(q) ||
        t.pickup_location?.address?.toLowerCase().includes(q) || t.dropoff_location?.address?.toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-100 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-[200px] bg-slate-50 rounded-lg px-3 py-2">
          <Search size={14} className="text-slate-400" />
          <input type="text" placeholder="Suchen..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            className="bg-transparent outline-none text-sm flex-1" data-testid="transfer-search" />
        </div>
        <select value={transferFilter} onChange={e => setTransferFilter(e.target.value)}
          className="bg-slate-50 rounded-lg px-3 py-2 text-sm outline-none" data-testid="transfer-filter">
          <option value="all">Alle Status</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Kunde</th>
              <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Route</th>
              <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Datum</th>
              <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Angebote</th>
              <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Status</th>
              <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase">Aktion</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {filtered.map(t => (
              <tr key={t.id} className="hover:bg-slate-50">
                <td className="px-5 py-3"><p className="font-medium text-slate-800 text-sm">{t.customer_name}</p><p className="text-xs text-slate-400">{t.customer_email}</p></td>
                <td className="px-5 py-3"><p className="text-sm text-slate-700 truncate max-w-[180px]">{t.pickup_location?.address?.split(',')[0]}</p><p className="text-xs text-slate-400 truncate max-w-[180px]">{t.dropoff_location?.address?.split(',')[0]}</p></td>
                <td className="px-5 py-3 text-sm text-slate-700">{formatPickupDate(t.pickup_date)}</td>
                <td className="px-5 py-3"><span className="px-2 py-0.5 bg-slate-100 rounded text-xs font-medium text-slate-600">{t.offers_count || 0}</span></td>
                <td className="px-5 py-3"><span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[t.status]}`}>{STATUS_LABELS[t.status]}</span></td>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-1">
                    <button onClick={() => onSelectTransfer(t)} className="p-1.5 bg-slate-100 hover:bg-slate-200 rounded" data-testid={`view-transfer-${t.id}`} title="Details"><Eye size={14} className="text-slate-600" /></button>
                    {t.status === 'booked' && (
                      <>
                        <button onClick={() => resendEmail(t.id, 'booking', 'customer')} disabled={resendingEmail === `${t.id}-booking-customer`}
                          className="p-1.5 bg-emerald-50 hover:bg-emerald-100 rounded disabled:opacity-50" data-testid={`resend-customer-${t.id}`} title="Buchungsbestätigung an Kunde senden">
                          {resendingEmail === `${t.id}-booking-customer` ? <Loader2 size={14} className="text-emerald-600 animate-spin" /> : <Mail size={14} className="text-emerald-600" />}
                        </button>
                        <button onClick={() => resendEmail(t.id, 'booking', 'driver')} disabled={resendingEmail === `${t.id}-booking-driver`}
                          className="p-1.5 bg-blue-50 hover:bg-blue-100 rounded disabled:opacity-50" data-testid={`resend-driver-${t.id}`} title="Buchungsbestätigung an Fahrer senden">
                          {resendingEmail === `${t.id}-booking-driver` ? <Loader2 size={14} className="text-blue-600 animate-spin" /> : <Send size={14} className="text-blue-600" />}
                        </button>
                      </>
                    )}
                    {(t.status === 'bidding' || (t.status === 'open' && t.offers_count > 0)) && (
                      <button onClick={() => resendEmail(t.id, 'offer')} disabled={resendingEmail === `${t.id}-offer-`}
                        className="p-1.5 bg-orange-50 hover:bg-orange-100 rounded disabled:opacity-50" data-testid={`resend-offer-${t.id}`} title="Angebote erneut an Kunde senden">
                        {resendingEmail === `${t.id}-offer-` ? <Loader2 size={14} className="text-orange-600 animate-spin" /> : <Mail size={14} className="text-orange-600" />}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
